import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// ────────────── Custom teardrop pin with the hive's accent color ──────────────

function pinIcon(color = "#EAB308", count = 1) {
  // Inline SVG → encoded as a DivIcon so we get the hive's accent color visibly
  // and clusters get a count badge.
  const safe = (color || "#EAB308").replace(/"/g, "");
  const isCluster = count > 1;
  const html = `
    <div style="position:relative;width:32px;height:42px;">
      <svg viewBox="0 0 32 42" width="32" height="42" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 1 C 7.7 1 1 7.7 1 16 c 0 11 15 25 15 25 s 15 -14 15 -25 C 31 7.7 24.3 1 16 1 z"
              fill="${safe}" stroke="#ffffff" stroke-width="2" />
        <circle cx="16" cy="16" r="5.5" fill="#ffffff" fill-opacity="0.92" />
      </svg>
      ${isCluster
        ? `<span style="position:absolute;top:-4px;right:-4px;min-width:18px;height:18px;border-radius:9px;background:#7c2d12;color:#fff;font-size:11px;line-height:18px;text-align:center;padding:0 4px;font-weight:600;border:2px solid #ffffff;">${count}</span>`
        : ""}
    </div>`;
  return L.divIcon({
    html,
    className: "hive-map-pin",  // no extra leaflet default styles
    iconSize: [32, 42],
    iconAnchor: [16, 41],
    popupAnchor: [0, -36],
  });
}

// ────────────── Auto-fit map to encompass all visible points ──────────────

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lon], 9, { animate: false });
      return;
    }
    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lon]));
    map.fitBounds(bounds, { padding: [30, 30], maxZoom: 9 });
  }, [points, map]);
  return null;
}

// ────────────── Group hives by yard → cluster pin per yard ──────────────

function groupByYard(hives) {
  // Hives without a yard each get their own group (so they show individually).
  // Multiple hives sharing a yard become one cluster pin at the yard centroid.
  const groups = new Map();
  for (const h of hives) {
    const yard = (h.yard || "").trim();
    const key = yard ? `yard:${yard.toLowerCase()}` : `solo:${h.id}`;
    if (!groups.has(key)) {
      groups.set(key, { yard, members: [] });
    }
    groups.get(key).members.push(h);
  }
  return [...groups.values()].map((g) => {
    const members = g.members;
    const lat = members.reduce((s, m) => s + m.lat, 0) / members.length;
    const lon = members.reduce((s, m) => s + m.lon, 0) / members.length;
    return {
      key: g.yard ? `yard-${g.yard}` : `solo-${members[0].id}`,
      yard: g.yard,
      lat,
      lon,
      members,
      // Pick the most "saturated" colour for the cluster — fall back to first member.
      color: members[0].color || "#EAB308",
    };
  });
}

// ────────────── Public component ──────────────

/**
 * Map of multiple hives. Hives sharing a yard cluster into one pin with a count badge.
 * Clicking a pin opens a popup listing all hives in that yard (or just one if solo).
 *
 * Props:
 *   hives: [{ id, name, color, lat, lon, location, strength, yard }]  (already filtered to pinned)
 *   height: number (px)
 *   linkable: if true, popup includes "Open hive →" links (false on public-share)
 */
export default function HiveMapView({ hives, height = 260, linkable = true, LinkComponent }) {
  const groups = useMemo(() => groupByYard(hives), [hives]);
  // RouterLink is optional — only rendered when linkable=true
  const Link = LinkComponent;

  if (!hives.length) return null;

  return (
    <div className="rounded-lg overflow-hidden border border-stone-200" style={{ height }} data-testid="hive-map-view">
      <MapContainer
        center={[hives[0].lat, hives[0].lon]}
        zoom={3}
        scrollWheelZoom={false}
        style={{ height: "100%", width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          subdomains="abcd"
          maxZoom={19}
        />
        <FitBounds points={hives} />
        {groups.map((g) => (
          <Marker
            key={g.key}
            position={[g.lat, g.lon]}
            icon={pinIcon(g.color, g.members.length)}
          >
            <Popup>
              <div className="text-xs space-y-1.5">
                {g.yard && g.members.length > 1 && (
                  <div className="font-medium text-stone-900">
                    {g.yard} <span className="text-stone-400">· {g.members.length} hives</span>
                  </div>
                )}
                <ul className="space-y-1">
                  {g.members.map((m) => (
                    <li key={m.id} className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ background: m.color, border: "1px solid #fff", boxShadow: "0 0 0 1px #d6d3d1" }}
                      />
                      <div className="min-w-0">
                        <div className="font-medium text-stone-900 truncate">{m.name}</div>
                        {m.location && <div className="text-stone-500 truncate">{m.location}</div>}
                        {m.strength && <div className="text-stone-400 capitalize">{m.strength}</div>}
                      </div>
                      {linkable && Link && (
                        <Link to={`/app/hives/${m.id}`} className="ml-auto text-amber-700 hover:underline shrink-0">Open →</Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}

// Convenience re-export so callers can use it as their map's loading skeleton state.
export function MapSkeleton({ height = 260 }) {
  const [pulse, setPulse] = useState(true);
  useEffect(() => { setPulse(true); }, []);
  return (
    <div className={`rounded-lg bg-stone-100 ${pulse ? "animate-pulse" : ""}`} style={{ height }} />
  );
}
